import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/state/AuthContext";
import { useUploadOperation } from "@/hooks/useUploadOperation";
import type { FileSelection } from "@/components/upload/fileSelection";
import { createWorkspaceDraftStore, imageEligible, type DraftSubmission, type WorkspaceDraftStore } from "@/lib/workspaceDraft";
import type { OperationKind } from "@/lib/operationReceipt";
export type { DraftSubmission } from "@/lib/workspaceDraft";

type Operation = ReturnType<typeof useUploadOperation>;
type Send = Parameters<Operation["submit"]>[0];
export type WorkspaceOperation = Omit<Operation, "submit"> & {
  submit: (send: Send, submission?: DraftSubmission) => ReturnType<Operation["submit"]>;
};
const pendingPhases = new Set(["unknown", "blocked", "submitting", "checking"]);
type Operations = {
  scan: WorkspaceOperation; checkup: WorkspaceOperation; working: boolean; blocked: boolean;
  blockers: { kind: OperationKind; operation: WorkspaceOperation }[];
};
const EntryPathContext = createContext<string | null>(null);
const DraftContext = createContext<WorkspaceDraftStore | null>(null);
const OperationsContext = createContext<Operations | null>(null);

export function WorkspaceDraftProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const owner = user && (typeof user.id === "string" || typeof user.id === "number") ? String(user.id) : "";
  // Remount local UI and pending closures on a real owner transition, before paint.
  return <OwnedWorkspace key={owner || "__signed_out__"} owner={owner}>{children}</OwnedWorkspace>;
}
function OwnedWorkspace({ owner, children }: { owner: string; children: ReactNode }) {
  const [store] = useState(() => createWorkspaceDraftStore(owner));
  const location = useLocation();
  const [entryPath, setEntryPath] = useState<string | null>(null);
  useEffect(() => {
    if (location.pathname === "/app/checkup") setEntryPath(location.pathname + location.search);
  }, [location.pathname, location.search]);
  const scan = useUploadOperation("scan", !!owner), checkup = useUploadOperation("checkup", !!owner);
  const active = useRef(false), lease = useRef(false);
  const pending = useRef(new Map<string, { kind: OperationKind; submission: DraftSubmission }>());
  const current = useRef({ scan, checkup }); current.current = { scan, checkup };
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const consume = useCallback((key: string | undefined) => {
    if (!active.current || !key) return;
    const capture = pending.current.get(key);
    if (capture) { pending.current.delete(key); store.complete(capture.submission); }
  }, [store]);
  const scanSnapshot = scan.snapshot, checkupSnapshot = checkup.snapshot;
  useEffect(() => {
    for (const snapshot of [scanSnapshot, checkupSnapshot]) {
      if (snapshot.accepted?.result_path) consume(snapshot.checkpoint?.requestKey);
    }
  }, [scanSnapshot, checkupSnapshot, consume]);
  function wrap(kind: OperationKind): WorkspaceOperation {
    const operation = current.current[kind];
    const blocked = Object.values(current.current).some(item => pendingPhases.has(item.snapshot.phase));
    return { ...operation, canSubmit: operation.canSubmit && !blocked,
      async submit(send, submission) {
        const original = current.current[kind];
        if (!active.current || !owner || lease.current || Object.values(current.current).some(item => pendingPhases.has(item.getSnapshot().phase))
          || !["idle", "rejected"].includes(original.getSnapshot().phase) || (submission && (submission.ownerId !== owner || submission.mode !== kind))) return null;
        lease.current = true;
        let key: string | undefined;
        try {
          const accepted = await original.submit(async requestKey => {
            key = requestKey;
            if (submission) pending.current.set(requestKey, { kind, submission });
            return send(requestKey);
          });
          if (!active.current) return null;
          if (accepted?.result_path) consume(key);
          else if (original.getSnapshot().phase === "rejected" && key) pending.current.delete(key);
          return accepted;
        } finally { lease.current = false; }
      },
      beginNew() {
        if (!active.current || lease.current || Object.values(current.current).some(item => item.working)) return false;
        const key = operation.getSnapshot().checkpoint?.requestKey;
        const started = operation.beginNew();
        // An explicitly closed receipt no longer needs to retain captured File objects.
        if (started && key) pending.current.delete(key);
        return started;
      },
    };
  }
  const scanView = wrap("scan"), checkupView = wrap("checkup");
  const blockers = ([{ kind: "scan" as const, operation: scanView }, { kind: "checkup" as const, operation: checkupView }])
    .filter(item => pendingPhases.has(item.operation.snapshot.phase));
  const operations = { scan: scanView, checkup: checkupView, blockers, blocked: blockers.length > 0,
    working: scan.working || checkup.working };
  return <EntryPathContext.Provider value={entryPath}><DraftContext.Provider value={store}><OperationsContext.Provider value={operations}>{children}</OperationsContext.Provider></DraftContext.Provider></EntryPathContext.Provider>;
}

export function useWorkspaceReturnPath(fallback: string): string {
  return useContext(EntryPathContext) || fallback;
}

export function useWorkspaceOperations() {
  const value = useContext(OperationsContext);
  if (!value) throw new Error("Workspace operations require WorkspaceDraftProvider");
  return value;
}
export function useWorkspaceDraft() {
  const contextStore = useContext(DraftContext);
  if (!contextStore) throw new Error("Workspace draft requires WorkspaceDraftProvider");
  const store: WorkspaceDraftStore = contextStore;
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return useMemo(() => {
    function selection(role: "primary" | "reference", imageOnly = false): FileSelection {
      const all = role === "primary" ? state.files : state.reference;
      const files = imageOnly ? all.filter(imageEligible) : all;
      return { files, duplicates: state.duplicates[role], overflow: state.overflow[role],
        add: incoming => store.add(role, incoming),
        remove: index => { const file = files[index]; if (file) store.remove(role, file); },
        clear: () => store.clearFiles(role, imageOnly),
        replace: files => store.replace(role, imageOnly ? [...state.files.filter(file => !imageEligible(file)), ...files] : files) };
    }
    return { picks: selection("primary"), reference: selection("reference"), imagePicks: selection("primary", true),
      retainedFiles: state.files.filter(file => !imageEligible(file)), statsText: state.statsText,
      setStatsText: store.setStatsText, referenceText: state.referenceText, setReferenceText: store.setReferenceText,
      project: state.project, setProject: store.setProject, clearProject: store.clearProject,
      hasDraft: !!(state.files.length || state.reference.length || state.statsText || state.referenceText),
      capture: store.capture, complete: store.complete, clear: store.clear };
  }, [state, store]);
}
